import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router'
import { z } from 'zod'

import { BrandArcs, Logo } from '@/shared/components/brand'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'

import { useAuth } from '../hooks/useAuth'
import { getAuthenticatedDestination } from '../utils/getAuthenticatedDestination'

/**
 * Esquema de validación del formulario.
 *
 * Se define con Zod y NO con validaciones sueltas dentro del componente,
 * por dos razones: es reutilizable (el mismo esquema podría validar la
 * respuesta del backend) y el tipo del formulario se DERIVA de él, así que
 * nunca se desincronizan.
 *
 * Los mensajes van en español porque los lee el usuario final.
 */
const loginSchema = z.object({
  email: z.email({ message: 'Ingresa un correo válido' }),
  password: z.string().min(1, { message: 'La contraseña es obligatoria' }),
})

/** El tipo sale del esquema. No se escribe a mano. */
type LoginFormData = z.infer<typeof loginSchema>

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { signIn, isLoading } = useAuth()
  const [generalError, setGeneralError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  /**
   * Si el usuario intentó entrar a una página protegida sin sesión,
   * ProtectedRoute guardó a dónde iba. Al autenticarse lo mandamos ahí en vez
   * de al inicio: pequeño detalle que mejora mucho la experiencia.
   */
  const attemptedDestination = (location.state as { from?: string } | null)?.from

  const onSubmit = async (data: LoginFormData) => {
    setGeneralError(null)
    try {
      const user = await signIn(data)
      void navigate(
        user.mustChangePassword
          ? '/required-password'
          : getAuthenticatedDestination(user.role, attemptedDestination),
        { replace: true },
      )
    } catch {
      setGeneralError('No se pudo iniciar sesión. Verifica tus credenciales.')
    }
  }

  return (
    <div className="bg-card flex min-h-screen flex-col lg:flex-row">
      {/* Panel de marca: solo en escritorio. Rail verde con logo, arcos y lema. */}
      <aside className="bg-sidebar text-sidebar-foreground border-sidebar-border relative hidden w-[44%] shrink-0 overflow-hidden border-r p-12 lg:flex lg:flex-col lg:justify-between">
        <BrandArcs
          corner="top-right"
          className="text-brand-gold/20 absolute -top-16 -right-16 size-[420px]"
        />
        <BrandArcs
          corner="bottom-left"
          className="text-brand-gold/10 absolute -bottom-24 -left-24 size-[520px]"
        />

        <Logo variant="horizontal" size={46} className="text-sidebar-foreground relative" />

        <div className="relative space-y-4">
          <p className="font-serif text-4xl leading-tight font-semibold text-balance">
            Disfruta tu siguiente viaje.
          </p>
          <p className="text-sidebar-foreground/70 max-w-sm text-sm">
            Panel de administración de Alto Palermo: clientes, viajes, pagos y caja de la agencia en
            un solo lugar.
          </p>
        </div>

        <p className="text-sidebar-foreground/40 relative text-xs tracking-[0.18em] uppercase">
          Travels &amp; Tours
        </p>
      </aside>

      {/* Columna del formulario. */}
      <main className="flex flex-1 items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm space-y-8">
          <div className="space-y-6 lg:hidden">
            <Logo variant="horizontal" size={40} />
          </div>

          <div className="space-y-1.5">
            <h1 className="font-serif text-3xl font-semibold tracking-tight">Iniciar sesión</h1>
            <p className="text-muted-foreground text-sm">
              Ingresa tus credenciales para continuar.
            </p>
          </div>

          {/* `void` marca explícitamente que ignoramos la promesa a propósito.
              Sin él, la regla no-floating-promises de ESLint marcaría un error
              — y con razón: una promesa ignorada por descuido es un bug común. */}
          <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="tu@correo.com"
                aria-invalid={errors.email !== undefined}
                {...register('email')}
              />
              {errors.email && (
                <p className="text-destructive text-sm" role="alert">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={errors.password !== undefined}
                {...register('password')}
              />
              {errors.password && (
                <p className="text-destructive text-sm" role="alert">
                  {errors.password.message}
                </p>
              )}
            </div>

            {generalError && (
              <p className="text-destructive text-sm" role="alert">
                {generalError}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Ingresando…' : 'Ingresar'}
            </Button>
          </form>
        </div>
      </main>
    </div>
  )
}
